import { FORM_ERROR } from 'final-form';
import { FC } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { Col, Row } from 'antd';
import { WithTranslation, withTranslation } from 'react-i18next';

import AddItemPageHeader from '~src/shared/headers/AddItemPageHeader';
import { PageContentContainer } from '~src/styles/containers.sc';
import logger from '~src/services/logger/LlsLogger';
import { addAttribute } from '~src/modules/attributes/actions/attributes.actions';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { getCompanyContentLocale } from '~src/services/session';
import { StoreI } from '~src/rootReducer';
import { AttributeAddI } from '~src/modules/attributes/interfaces';
import { AttributeI } from '~src/interfaces/attributes.types';
import { routes } from '~src/routingConfig';

import AttributeAddForm from './AttributeAddForm';

interface DispatchProps {
  addAttribute(attribute: AttributeAddI, defaultContentLocale: string): ApiReduxResp<AttributeI>;
}

interface StateProps {
  defaultContentLocale: string;
}

type Props = DispatchProps & StateProps & WithTranslation & RouteComponentProps<any>;

const AttributeAdd: FC<Props> = props => {
  const onSubmit = (values: AttributeAddI) =>
    props
      .addAttribute(values, props.defaultContentLocale)
      .then(({ response, error }) => {
        if (error) {
          const attributeExists = (message: string) => /^attribute '(.*)' already exists$/i.test(message);
          let mappedError = error.response && error.response.data.error;

          if (attributeExists(mappedError)) {
            mappedError = 'attribute_name.unique';
          }

          return {
            attributeName: mappedError,
            [FORM_ERROR]: 'add attribute option failed!',
          };
        }
        if (response) {
          props.history.push(`${routes.company.attributes}/${response.data.uuid}`);
        }
      })
      .catch(error => {
        logger.info('Error adding Attribute', {
          error,
          info: {
            componentStack: 'AttributesAdd.jsx',
          },
        });
        throw error;
      });

  return (
    <PageContentContainer>
      <Row gutter={30}>
        <Col span={12} offset={6}>
          <AddItemPageHeader
            t={props.t}
            titleKey="x.attributes.add.title"
            subTitleKey="x.attributes.add.subtitle"
          />
        </Col>
      </Row>
      <Row gutter={30}>
        <Col span={24}>
          <AttributeAddForm t={props.t} onSubmit={onSubmit} history={props.history} />
        </Col>
      </Row>
    </PageContentContainer>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  defaultContentLocale: getCompanyContentLocale(state),
});

const dispatchMapToProps = (dispatch: any): DispatchProps => ({
  addAttribute: (attribute, defaultContentLocale) => dispatch(addAttribute(attribute, defaultContentLocale)),
});

const AttributeAddT = withTranslation()(AttributeAdd);
const AttributeAddC = connect(mapStateToProps, dispatchMapToProps)(AttributeAddT);

export default AttributeAddC;
